
































































































import Vue from 'vue'
import Component from 'vue-class-component'
import {Prop, Watch} from 'vue-property-decorator'
import {State} from 'vuex-class'
import AppRelease from '@/modules/app-router/model/AppRelease'
import {Colors, notify} from '@/modules/core/notification'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import ReleaseUserGroups from '@/modules/app-router/components/AppRelease/ReleaseUserGroups.vue'
import ApiVersionList from '@/modules/app-router/components/ApiVersion/ApiVersionList.vue'
import ApiVersionSelect from '@/modules/app-router/components/ApiVersion/ApiVersionSelect.vue'
import ConfirmDeleteRelease from '@/modules/app-router/components/AppRelease/ConfirmDeleteRelease.vue'
import ApiVersion from '@/modules/app-router/model/ApiVersion'
import SimpleMDE from 'simplemde'
import 'simplemde/dist/simplemde.min.css'
import ChangeDetail from '@/modules/app-router/components/Changes/ChangeDetail.vue';
import ChangeList from '@/modules/app-router/components/Changes/ChangeList.vue';

/**
 * Detalle de una distribución
 *
 * Muestra al usuario una pantalla con la información de una distribución y
 * permite editar sus campos.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {
        ChangeList, ChangeDetail, ConfirmDeleteRelease, ApiVersionSelect, ApiVersionList, ReleaseUserGroups},
})
export default class AppReleaseDetail extends Vue {
    public $refs: Vue['$refs'] & {
        form: {
            validate (): boolean,
            resetValidation (): void,
            update (field: string, value: any): void,
        },
        markdownContainer: HTMLDivElement,
    }

    /**
     * Controla la visibilidad de la pantalla
     */
    @Prop({type: Boolean, default: false})
    public value: boolean

    /**
     * Distribución de la aplicación actualmente mostrada por el componente
     */
    @State(state => state.appRouter.release)
    public release: AppRelease

    public markdown: SimpleMDE | null = null
    public previewMode = false

    /**
     * Guardando
     *
     * Indica si la aplicación se encuentra guardando los cambios que el usuario ha
     * realizado sobre la distribución en edición.
     */
    public saving = false

    public confirmDelete = false

    /**
     * Reglas de validación para los campos de la distribución
     */
    public rules: KeyValuePair<Array<(value: string) => true | string>> = {
        name: [
            (v: string) => !!v || 'El nombre de la distribución es obligatorio',
        ],
        // El número de versión es obligatorio y debe tener un formato acorde con el versionamiento
        // semántico
        version: [
            v => !!v || 'El número de versión es obligatorio',
            v => /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(-(0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(\.(0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*)?(\+[0-9a-zA-Z-]+(\.[0-9a-zA-Z-]+)*)?$/.test(v)
                || 'El número de versión no es válido',
        ],
        distFile: [
            (value: any) => !!value || 'El archivo de distribución es obligatorio',
        ],
        apiVersion: [
            (value: ApiVersion | null | any) => {
                return (!!value && !!value.id) || 'Es obligatorio seleccionar una versión de la API'
            },
        ],
    }

    public get enabled () {
        return this.release.enabled
    }

    public set enabled (enabled: boolean) {
        this.$refs.form.update('enabled', enabled)
    }


    public get isDefault () {
        return this.release.default
    }

    public set isDefault (enabled: boolean) {
        this.$refs.form.update('default', enabled)
    }

    @Watch('value')
    public watchValue (value: boolean) {
        if (value && this.$refs.form) {
            this.$refs.form.resetValidation()
        }

        if (value && this.markdown) {
            // Espera al siguiente ciclo de la interfaz para actualizar el valor del
            // editor de Markdown. Si el valor es asignado inmediatamente los
            // cambios no se verán reflejados hasta que el usuario haga
            // click sobre el componente.
            this.$nextTick(() => this.markdown!.value(this.release.changelog))
        }
    }

    /**
     * Monta en la interfaz el editor de Markdown
     */
    public renderMarkdown () {
        // Ya existe una referencia al editor de Markdown
        if (this.markdown) {
            this.markdown.value(this.release.changelog)

            return
        }

        this.markdown = new SimpleMDE({
            element: this.$refs.markdownContainer,
            spellChecker: false,
        })

        // Actualiza el AppRelease.changelog cuando se produce un cambio en el
        // editor.
        this.markdown.codemirror.on('change', () => {
            this.$refs.form.update('changelog', this.markdown!.value())
        })

        this.markdown.value(this.release.changelog)
    }

    /**
     * Guarda los cambios de la distribución. Si esta es nueva será registrada
     * en la aplicación
     */
    public async save () {
        if (!this.$refs.form.validate()) {
            return
        }

        this.saving = true
        await this.$store.dispatch('appRouter/saveAppRelease')

        notify(`La publicación con la versión ${this.release.version} se ha registrado correctamente`)
        this.$emit('input', false)
        this.saving = false
    }

    /**
     * Captura cualquier error producido durante la ejecución del componente y
     * notifica al usuario.
     *
     * @param error
     */
    public errorCaptured (error: Error) {
        this.saving = false
        notify(error.message, Colors.Error)
    }
}
